import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import RichText from "@/components/RichText";
import { Input } from "@/components/ui/input";
import { publicUrl } from "@/lib/storage";
import FavoriteButton from "@/components/FavoriteButton";

type Cause = {
  id: string;
  name: string;
  blurb: string | null;
  logo_path: string | null;
  athleteNames: string[];
};

const Causes = () => {
  const [causes, setCauses] = useState<Cause[]>([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [cRes, caRes, aRes] = await Promise.all([
        supabase.from("causes").select("*").eq("published", true).order("sort_order"),
        supabase.from("cause_athletes").select("cause_id, athlete_id"),
        supabase.from("athletes").select("id, name").eq("published", true),
      ]);
      const athleteNameById: Record<string, string> = {};
      (aRes.data ?? []).forEach((a: any) => { athleteNameById[a.id] = a.name; });
      const links: Record<string, string[]> = {};
      ((caRes.data as any) ?? []).forEach((r: any) => { (links[r.cause_id] ||= []).push(r.athlete_id); });
      const list: Cause[] = ((cRes.data as any) ?? []).map((c: any) => ({
        id: c.id,
        name: c.name,
        blurb: c.blurb,
        logo_path: c.logo_path,
        athleteNames: (links[c.id] ?? (c.athlete_id ? [c.athlete_id] : []))
          .map((id: string) => athleteNameById[id])
          .filter(Boolean),
      }));
      setCauses(list);
      setLoading(false);
    })();
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return causes;
    return causes.filter((c) => {
      const hay = [c.name, c.blurb ?? "", ...(c.athleteNames ?? [])].join(" ").toLowerCase();
      return hay.includes(s);
    });
  }, [causes, q]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container py-16">
        <header className="mb-10 max-w-2xl">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Give back</span>
          <h1 className="text-5xl md:text-6xl mt-3">Causes that matter to them.</h1>
          <p className="text-muted-foreground mt-3">
            Every cause is championed by an athlete on our roster.
          </p>
        </header>

        <div className="mb-8 max-w-md">
          <Input
            type="search"
            placeholder="Search by cause or athlete…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        {loading ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-muted-foreground">No causes match your search.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((c) => {
              const blurb = c.blurb ?? "";
              const isHtml = /<\/?[a-z][\s\S]*>/i.test(blurb);
              const logo = publicUrl(c.logo_path);
              const backedBy =
                c.athleteNames.length === 0
                  ? ""
                  : c.athleteNames.length === 1
                  ? `Backed by ${c.athleteNames[0]}`
                  : c.athleteNames.length === 2
                  ? `Backed by ${c.athleteNames[0]} & ${c.athleteNames[1]}`
                  : `Backed by ${c.athleteNames[0]} + ${c.athleteNames.length - 1} others`;
              return (
                <Link
                  key={c.id}
                  to={`/cause/${c.id}`}
                  className="relative bg-card rounded-[1.5rem] p-6 shadow-card hover:shadow-bold transition-shadow flex flex-col block"
                >
                  <div className="absolute top-3 right-3">
                    <FavoriteButton kind="cause" id={c.id} size="sm" />
                  </div>
                  {logo && (
                    <div className="w-16 h-16 rounded-xl bg-muted overflow-hidden mb-4 flex items-center justify-center">
                      <img src={logo} alt={`${c.name} logo`} className="w-full h-full object-contain" />
                    </div>
                  )}
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {backedBy}
                  </span>
                  <h3 className="font-display text-2xl mt-2">{c.name}</h3>
                  <div className="text-sm text-muted-foreground mt-2 flex-1 line-clamp-3">
                    {isHtml ? <RichText html={blurb} /> : <p>{blurb}</p>}
                  </div>
                  <span className="mt-5 inline-block text-xs font-semibold uppercase tracking-wider text-primary">
                    Learn more →
                  </span>
                </Link>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Causes;
